<template>
    <div class="tabs-box">
        <div class="tabs-menu">
            <ElTabs v-model="tabsMenuValue" type="card" @tab-click="tabClick" @tab-remove="tabRemove">
                <ElTabPane
                    v-for="item in tabsMenuList"
                    :key="item.path"
                    :label="item.title"
                    :name="item.path"
                    :closable="item.close"
                >
                    <template #label>
                        <span class="tabs-text">
                            {{ item.title }}
                        </span>
                    </template>
                </ElTabPane>
            </ElTabs>
            <MoreButton
                ref="tabMoreButtonRef"
                class="tab-more-button"
                popper-class="tab-more-button-menu"
                trigger="contextmenu"
                :teleported="true"
            ></MoreButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { ElTabPane, ElTabs } from "element-plus";
import Sortable from "sortablejs";
import { useRoute, useRouter } from "vue-router";

import type { TabPaneName, TabsPaneContext } from "element-plus";

import { type TabsMenuProps, useTabsStore } from "@/stores/tabs";
import { useKeepAliveStore } from "@/stores/keepAlive";
import MoreButton from "@/views/layouts/tabs/components/moreButton.vue";
import { HOME_URL } from "@/config";
import { useUserStore } from "@/stores/user";
import { useHistoryRouteStore } from "@/stores/historyRoute";

const route = useRoute();
const router = useRouter();
const tabStore = useTabsStore();
const keepAliveStore = useKeepAliveStore();
const historyRouteStore = useHistoryRouteStore();

const tabMoreButtonRef = ref<InstanceType<typeof MoreButton> | null>(null);

const tabsMenuValue = ref(route.fullPath);
const tabsMenuList = computed(() => tabStore.tabsMenuList);

onMounted(() => {
    tabsDrop();
});

// 注册tab右键菜单事件
const addTabContextmenu = (singleNode?: HTMLElement) => {
    nextTick(() => {
        if (singleNode) {
            singleNode.addEventListener("contextmenu", ($e: Event) => openDropdown($e as Event));
        } else {
            const nodeList = document.querySelectorAll(".tabs-menu .el-tabs__item");
            nodeList.forEach(node => {
                node.addEventListener("contextmenu", ($e: Event) => openDropdown($e as Event));
            });
        }
    });
};

// tabs 拖拽排序
const tabsDrop = () => {
    try {
        Sortable.create(document.querySelector(".el-tabs__nav") as HTMLElement, {
            draggable: ".el-tabs__item",
            animation: 300,
            onEnd({ newIndex, oldIndex }) {
                const tabsList = [...tabStore.tabsMenuList];
                const currRow = tabsList.splice(oldIndex as number, 1)[0];
                tabsList.splice(newIndex as number, 0, currRow);
                tabStore.setTabs(tabsList);
            }
        });
    } catch (e) {
        console.error(e);
    }
};

// Tab Click
const tabClick = (tabItem: TabsPaneContext) => {
    const fullPath = tabItem.props.name as string;
    router.push(fullPath);
};

// Remove Tab
const tabRemove = (fullPath: TabPaneName) => {
    const name = tabStore.tabsMenuList.find(item => item.path === fullPath)?.name || "";
    keepAliveStore.removeKeepAliveName(name);
    tabStore.removeTabsByName(name as string, undefined, fullPath === route.fullPath);
    // if (!keepAliveStore.keepAliveName.length) router.push(HOME_URL);
    // 如果删除所有的页签则跳转到首页
    if (tabStore.tabsMenuList.length === 0) {
        router.push(HOME_URL);
    }
};

// 打开右键菜单
const openDropdown = ($event: Event) => {
    $event.preventDefault();
    tabMoreButtonRef.value?.handleOpen($event);
};

// 监听路由的变化（防止浏览器后退/前进不变化 tabsMenuValue）
watch(
    () => route.fullPath,
    () => {
        // 6. 建立websocket连接 权限变更时自动刷新userinfo
        // tabStore.connectWebsocket();
        // if (route.meta.isFull) return;
        tabsMenuValue.value = route.fullPath;
        const tabsParams: TabsMenuProps = {
            icon: route.meta.icon as string,
            title: route.meta.title as string,
            path: route.fullPath,
            name: route.name as string,
            close: !route.meta.isAffix
        };
        tabStore.addTabs(tabsParams);
        historyRouteStore.addHistoryRoute(route);
        // 配置keepAlive：true时添加缓存
        route.meta.keepAlive && keepAliveStore.addKeepAliveName(route.name as string);
        nextTick(() => {
            const tabId = `tab-${tabsParams.path}`;
            const tabDom = document.querySelector(`#${CSS.escape(tabId)}`);
            if (tabDom) addTabContextmenu(tabDom as HTMLElement);
        });
    },
    { immediate: true }
);

onMounted(() => {
    addTabContextmenu();
});

const userStore = useUserStore();
const isEnterpriseAuth = computed(() => userStore.isEnterpriseAuth);
watch(
    () => isEnterpriseAuth,
    () => {
        // 解除组织机构认证后，当前页面如没权限，需跳转到有权限的第一个菜单
        // 路由没有权限，跳到账号中心
        if (!isEnterpriseAuth.value) {
            router.push({ name: "Me" });
        }
    },
    { deep: true }
);
</script>

<style scoped lang="scss">
@use "./index.scss";
</style>
